import { CatCampaignRecord } from '@/types';
import { Box, Chip, Tooltip } from '@mui/material';
import { GridColDef, gridDateComparator } from '@mui/x-data-grid';
import moment from 'moment';
import Avatar from 'react-avatar';
import { Link } from 'react-router';

import { ListSummary } from '@/types/shared';
import {
  listContactsTotalFilterOperators,
  listContactsTotalSortComparator,
} from '@/util/gridFilterOperators';
import ActionsMenu from './ActionsMenu';

export type ListTableColumnName =
  | 'List Name'
  | 'Date Created'
  | 'Assignees'
  | 'Coordinators'
  | 'Status'
  | 'id'
  | 'Created By'
  | 'Contacts'
  | 'Campaign';

type ListColumn<T> = GridColDef<ListSummary, T, string>;

const listName: ListColumn<string> = {
  headerName: 'List Name',
  field: 'name',
  align: 'left',
  flex: 6,
  renderCell: (params) => (
    <Link
      style={{ textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)' }}
      to={`/lists/${params.row.id}`}
    >
      {params.row.name}
    </Link>
  ),
};

const listNameDashboard: ListColumn<string> = {
  headerName: 'List Name',
  field: 'name',
  align: 'left',
  flex: 9,
  renderCell: (params) => (
    <Link
      style={{ textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)' }}
      to={`/lists/${params.row.id}/dashboard`}
    >
      {params.row.name}
    </Link>
  ),
};

const dateCreated: ListColumn<string> = {
  headerName: 'Date Created',
  field: 'createdAt',
  align: 'left',
  flex: 3,
  type: 'date',
  // MUI v6 date columns require Date; valueGetter converts string to Date
  valueGetter: ((value: string) =>
    value ? new Date(value as string) : null) as unknown as ListColumn<string>['valueGetter'],
  sortComparator: gridDateComparator,
  renderCell: (params) => (
    <>{params.row.createdAt ? moment(params.row.createdAt).format('DD/MM/YYYY') : ''}</>
  ),
};

const assignees: ListColumn<string> = {
  headerName: 'Assignees',
  field: 'assignees',
  align: 'left',
  flex: 6,
  valueGetter: (value, row) => row.assignees.map((assignee) => assignee.name).join(', '),
  renderCell: (params) => {
    return (
      <>
        {params.row.assignees.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              width: "100%",
              alignItems: "center"
            }}>
            {params.row.assignees.length}
            <Box
              sx={{
                display: "flex",
                gap: "2px"
              }}>
              {params.row.assignees.map((assignee, idx) => (
                <Avatar src={assignee.picture} name={assignee.name} size="40" round key={idx} />
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  },
};

const coordinators: ListColumn<string> = {
  headerName: 'Coordinators',
  field: 'coordinators',
  align: 'left',
  flex: 6,
  valueGetter: (value, row) => row.coordinators.map((coordinator) => coordinator.name).join(', '),
  renderCell: (params) => {
    return (
      <>
        {params.row.coordinators.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              width: "100%",
              alignItems: "center"
            }}>
            {params.row.coordinators.length}
            <Box
              sx={{
                display: "flex",
                gap: "2px"
              }}>
              {params.row.coordinators.map((coordinator, idx) => (
                <Avatar
                  src={coordinator.picture}
                  name={coordinator.name}
                  size="40"
                  round
                  key={idx}
                />
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  },
};

import { getStatusFromCounts } from '@/util/listStatus';

export { getStatusFromCounts };

const status: ListColumn<string> = {
  headerName: 'Status',
  field: 'status',
  align: 'left',
  flex: 3,
  type: 'singleSelect',
  valueOptions: ['Complete', 'Not started', 'In progress'],
  valueGetter: (value, row) =>
    getStatusFromCounts({ totalCount: row.count, uncalledCount: row.uncalledCount }),
  renderCell: ({ value }) => {
    switch (value) {
      case 'Complete':
        return <Chip label={value} color={'primary'} size="small" variant={'filled'} />;
      case 'Not started':
        return <Chip label={value} color={'warning'} size="small" variant={'filled'} />;
      default:
        return <Chip label={value} color={'secondary'} size="small" variant={'filled'} />;
    }
  },
};

const contacts: ListColumn<string> = {
  headerName: 'Contacts',
  field: 'contacts',
  align: 'left',
  flex: 2,
  valueGetter: (value, row) => `${row.count - row.uncalledCount}/${row.count}`,
  filterOperators: listContactsTotalFilterOperators,
  sortComparator: listContactsTotalSortComparator,
};

const id: ListColumn<string> = {
  headerName: 'Id',
  field: 'id',
  align: 'left',
  headerAlign: 'left',
  type: 'number',
  flex: 3,
  editable: false,
  valueFormatter: (value: unknown) =>
    value == null || value === '' || (typeof value === 'number' && Number.isNaN(value))
      ? ''
      : String(value),
};

const createdBy: ListColumn<string> = {
  headerName: 'Created By',
  field: 'created_by',
  flex: 1,
};

const campaign: ListColumn<string> = {
  headerName: 'Campaign',
  field: 'campaign',
  valueGetter: (value, row) => row.campaign?.name ?? '',
  flex: 3,
  editable: false,
  align: 'left',
  renderCell: (params) => {
    return params.row.campaign ? (
      <Tooltip title={params.row.campaign?.label}>
        <span>{params.value}</span>
      </Tooltip>
    ) : undefined;
  },
};

const ListColumns: Record<ListTableColumnName, ListColumn<string>> = {
  'List Name': listName,
  Contacts: contacts,
  'Created By': createdBy,
  'Date Created': dateCreated,
  Assignees: assignees,
  Coordinators: coordinators,
  Status: status,
  id,
  Campaign: campaign,
};

export const getListTableColumnDefinitions = (
  columnNames: ListTableColumnName[],
  options?: { toDashboard?: boolean }
) => {
  return columnNames.map((column) =>
    column === 'List Name' && options?.toDashboard ? listNameDashboard : ListColumns[column]
  );
};

export const getListTableColumnDefinitionsWithOverrides = (
  columnNames: ({ name: ListTableColumnName } & Partial<ListColumn<string>>)[]
): ListColumn<string>[] => {
  return columnNames.map(({ name, ...overrides }) => ({ ...ListColumns[name], ...overrides }));
};

export const createActionsColumn = ({
  handleDelete,
  handleEdit,
  handleOpen,
  handleComplete,
}: {
  handleEdit: () => void;
  handleDelete: () => void;
  handleComplete: () => void;
  handleOpen: (id: string, name: string, campaign?: CatCampaignRecord) => void;
}): ListColumn<string> => ({
  headerName: '',
  field: 'Actions',
  align: 'right',
  flex: 1.5,
  type: 'actions',
  sortable: false,
  headerClassName: 'lastcolumnSeparator',
  renderCell: ({ row }) => (
    <ActionsMenu
      rowId={String(row.id)}
      name={row.name}
      campaign={row.campaign ?? undefined}
      handleOpen={handleOpen}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      handleComplete={handleComplete}
      disableDelete={row.uncalledCount < row.count}
    />
  ),
});
